import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { TaskData } from "@/entities/tasks/types";
import { useAPIMutation } from "@/services/api/hook";
import { createUseFilteredEntitiesInfinite } from "@/services/api/infinite";
import { EmptyFilters, Filters, SearchQuery } from "@/services/api/types";
import useBoundStore, {
  MAX_ENTITIES_PER_PAGE
} from "@/services/store/BoundStore";
import { useUsersMe } from "@/entities/users/hooks";
import { formatISO } from "date-fns";

export const MAX_TASKS_PER_PAGE = MAX_ENTITIES_PER_PAGE;

export const { useFilteredEntitiesInfinite } =
  createUseFilteredEntitiesInfinite<TaskData>("tasks", MAX_TASKS_PER_PAGE);

// Refetches task lists while keeping the current rows on screen; a reset
// blanks them back to the cached snapshot, which still holds the old rows.
// Unmounted lists are only invalidated and refetch when they mount again, so
// one change doesn't fetch every list the session has cached.
export const useRefreshFilteredTasks = () => {
  const queryClient = useQueryClient();
  return useCallback(() => {
    const queryKey = ["tasks/filter"];
    // refetchType "active" skips lists whose only observers are disabled.
    const skippedLists = queryClient
      .getQueryCache()
      .findAll({ queryKey })
      .filter((query) => query.isDisabled() && query.state.data !== undefined);
    return Promise.all([
      queryClient.invalidateQueries({ queryKey, refetchType: "active" }),
      ...skippedLists.map((query) => {
        // Cancel an in-flight fetch, which may predate the change, like refetchQueries does.
        const fetched = query
          .fetch(undefined, { cancelRefetch: true })
          .catch(() => undefined);
        // Like refetchQueries, don't hold the refresh open on a fetch paused offline.
        return query.state.fetchStatus === "paused" ? undefined : fetched;
      })
    ]).then(() => undefined);
  }, [queryClient]);
};

const isTaskListData = (
  data: unknown
): data is { pages: Record<string, unknown>[] } =>
  !!data && Array.isArray((data as { pages?: unknown }).pages);

// Drops a deleted task from every cached task list before refetching, so the
// row is gone at once and never returns from the pre-refetch data.
export const useRemoveTaskFromLists = () => {
  const queryClient = useQueryClient();
  const refreshTasks = useRefreshFilteredTasks();
  return useCallback(
    (taskId: string) => {
      queryClient.setQueriesData<unknown>(
        { queryKey: ["tasks/filter"] },
        (data: unknown) =>
          isTaskListData(data)
            ? {
                ...data,
                pages: data.pages.map((page) =>
                  Array.isArray(page.tasks)
                    ? {
                        ...page,
                        tasks: (page.tasks as TaskData[]).filter(
                          (task) => task.id !== taskId
                        )
                      }
                    : page
                )
              }
            : data
      );
      return refreshTasks();
    },
    [queryClient, refreshTasks]
  );
};

export const useFilteredTasks = (
  filters: Filters = EmptyFilters,
  search?: SearchQuery,
  groupedBy?: keyof TaskData,
  pageSize = MAX_TASKS_PER_PAGE,
  sortField?: string,
  sortAsc?: boolean,
  fetchAll = false,
  enabled = true
) => {
  return useFilteredEntitiesInfinite(
    () => ({ addEntity: useBoundStore((state) => state.tasks.addEntity) }),
    {
      filters,
      search,
      groupedBy,
      pageSize,
      sortField,
      sortAsc,
      fetchAll,
      enabled,
      refetchInvalidatedOnMount: true
    }
  );
};

// Overdue = assigned to the current user, still open, due strictly before
// today. Drives the sidebar "Tasks" nav badge.
export const useOverdueTasksCount = (enabled = true) => {
  const userId = useUsersMe().data?.id;

  const overdueFilters = userId
    ? {
        assigneeId: userId,
        isCompleted: false,
        dueDate: { lt: formatISO(new Date(), { representation: "date" }) }
      }
    : EmptyFilters;

  const { total = 0 } = useFilteredTasks(
    overdueFilters,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    enabled && !!userId
  );

  return total;
};

export const useCreateTaskMutation = () => {
  return useAPIMutation<
    {
      data: Partial<TaskData>;
    },
    {
      id: string;
    }
  >("tasks", {
    method: "POST"
  });
};

export const useUpdateTaskMutation = () => {
  return useAPIMutation<
    {
      pathParams: {
        taskId: string;
      };
      data: Partial<TaskData>;
    },
    TaskData
  >("tasks/update", {
    method: "PUT"
  });
};

export const useDeleteTaskMutation = () => {
  return useAPIMutation<{ pathParams: { taskId: string } }, void>(
    "tasks/update",
    {
      method: "DELETE"
    }
  );
};

export const useUndoDeleteTaskMutation = () => {
  return useAPIMutation<
    {
      pathParams: {
        taskId: string;
      };
    },
    TaskData
  >("tasks/undoDelete", {
    method: "PUT"
  });
};

export const useDreamerUndoTaskMutation = () => {
  return useAPIMutation<
    {
      pathParams: {
        taskId: string;
      };
    },
    void
  >("tasks/dreamerUndo", {
    method: "PUT"
  });
};

export const useDreamerReopenTaskMutation = () => {
  return useAPIMutation<
    {
      pathParams: {
        taskId: string;
      };
    },
    void
  >("tasks/dreamerReopen", {
    method: "PUT"
  });
};

export const useDreamerCompleteTaskMutation = () => {
  return useAPIMutation<
    {
      pathParams: {
        taskId: string;
      };
    },
    void
  >("tasks/dreamerComplete", {
    method: "PUT"
  });
};
